import { motion } from "motion/react";
import { useExtracted } from "next-intl";
import {
  annualPerMonth,
  annualSavingsPercent,
  formatUsd,
  type Duration,
  type PricingOffer,
} from "@/lib/pricing";
import { cn } from "@/lib/utils";

interface DurationSelectorProps {
  selected: string;
  onSelect: (duration: Duration) => void;
  offer: PricingOffer | null;
  /** Creator link or approved creator: annual at the creator price. */
  creatorAnnual?: boolean;
}

/** Prices a plan for this visitor, with the standard price it replaces. */
export function planPrice(
  offer: PricingOffer,
  duration: Duration,
  creatorAnnual = false,
): { cents: number; was: number | null } {
  let cents = offer.prices[duration];
  if (duration === "1y" && creatorAnnual && offer.creator_annual_cents) {
    cents = Math.min(cents, offer.creator_annual_cents);
  }
  const standard = offer.standard_prices[duration];
  return { cents, was: cents < standard ? standard : null };
}

export function DurationSelector({
  selected,
  onSelect,
  offer,
  creatorAnnual = false,
}: DurationSelectorProps) {
  const te = useExtracted();
  const monthly = offer ? planPrice(offer, "1m") : null;
  const annual = offer ? planPrice(offer, "1y", creatorAnnual) : null;
  const tiers: Array<{
    key: Duration;
    price: string;
    was: string | null;
    label: string;
    detail: string;
    highlighted?: boolean;
  }> = [
    {
      key: "1m",
      price: monthly ? formatUsd(monthly.cents) : "—",
      was: monthly?.was ? formatUsd(monthly.was) : null,
      label: te("Per month"),
      detail: te("Cancel anytime"),
    },
    {
      key: "1y",
      price: annual ? formatUsd(annual.cents) : "—",
      was: annual?.was ? formatUsd(annual.was) : null,
      label: te("Per year"),
      detail:
        annual && offer
          ? te("About {price}/month · save {percent}%", {
              price: annualPerMonth(annual.cents),
              percent: String(
                annualSavingsPercent({
                  "1m": offer.prices["1m"],
                  "1y": annual.cents,
                }),
              ),
            })
          : "",
      highlighted: true,
    },
  ];

  return (
    <div className="grid w-full grid-cols-2 gap-1.5 rounded-[24px] border border-white/10 bg-black/28 p-1.5 shadow-[inset_0_1px_0_rgba(255,255,255,0.07),0_20px_56px_rgba(0,0,0,0.24)] backdrop-blur-xl">
      {tiers.map(({ key, price, was, label, detail, highlighted }, index) => (
        <motion.button
          animate={{ opacity: 1, y: 0 }}
          aria-pressed={selected === key}
          className={cn(
            "relative flex min-h-28 cursor-pointer flex-col items-center justify-center overflow-visible rounded-[18px] border px-2 py-3 text-center transition-[border-color,background-color,box-shadow] duration-200",
            selected === key
              ? "border-cyan-100/28 bg-[linear-gradient(135deg,rgba(143,232,244,0.09),rgba(174,188,248,0.055)_52%,rgba(191,116,242,0.1))] shadow-[inset_0_1px_0_rgba(255,255,255,0.12),0_8px_24px_rgba(102,178,255,0.06)]"
              : "border-transparent bg-transparent hover:border-white/10 hover:bg-white/[0.025]",
            highlighted &&
              "shadow-[0_0_28px_rgba(143,232,244,0.12)] ring-1 ring-cyan-100/25",
          )}
          initial={{ opacity: 0, y: 14 }}
          key={key}
          transition={{ duration: 0.4, delay: index * 0.06 }}
          type="button"
          onClick={() => onSelect(key)}
        >
          {highlighted ? (
            <span className="absolute top-0 left-1/2 z-10 inline-flex -translate-x-1/2 -translate-y-1/2 rounded-full bg-[linear-gradient(90deg,rgba(143,232,244,0.58),rgba(174,188,248,0.34)_52%,rgba(191,116,242,0.58))] p-px shadow-[0_0_16px_rgba(155,185,255,0.08)]">
              <span className="block rounded-full bg-[linear-gradient(100deg,rgba(11,27,34,0.92),rgba(27,15,39,0.9))] px-2.5 py-0.5 text-[10px] font-semibold tracking-[0.08em] whitespace-nowrap text-white/85">
                {te("Most popular")}
              </span>
            </span>
          ) : null}
          <span className="mt-2 flex items-baseline gap-1.5">
            <span className="font-mono-numbers text-3xl text-white/94">
              {price}
            </span>
            {was ? (
              <span className="font-mono-numbers text-sm text-white/35 line-through">
                {was}
              </span>
            ) : null}
          </span>
          <span className="mt-1 text-[11px] font-semibold tracking-[0.08em] text-white/55">
            {label}
          </span>
          <span className="mt-2 min-h-4 text-[11px] text-white/45">
            {detail}
          </span>
        </motion.button>
      ))}
    </div>
  );
}
